<script lang="ts" setup>
  import { computed } from 'vue'
  import BaseAccordion from '@/shared/components/BaseAccordion/BaseAccordion.vue'
  import BaseChip from '@/shared/components/BaseChip/BaseChip.vue'
  import { TOGGLE_EVENT_NAME } from './FlowAccordion.constants'
  import FlowAccordionBenefit from './components/FlowAccordionBenefit/FlowAccordionBenefit.vue'
  import { useDiscount, useGoogleAnalytics } from '@/shared/composables'
  import AddonText from '@/shared/components/AddonText/AddonText.vue'
  import type { Plan } from '@/shared/types/api.types'
  import { useUserStore } from '@/shared/store'
  import { toFixedNumber } from '@/shared/utils'
  import { TravelDirection } from '@/shared/types/common.types'
  import { useI18n } from 'vue-i18n'
  import { CYBER_ADDON_ID } from '@/shared/constants/addons.constants'
  import CyberSecurityButton from '@/entities/Flow/components/CyberSecurityModal/CyberSecurityButton.vue'

  interface Props {
    title?: string
    withAddons?: boolean
    withPrice?: boolean
    plan: Plan
    isQuotePage?: boolean
    typeOfTravel: TravelDirection
  }

  interface Emits {
    (e: typeof TOGGLE_EVENT_NAME, isOpen: boolean, rowId: string, rowName: string): void
  }

  const props = withDefaults(defineProps<Props>(), {
    withAddons: true,
    withPrice: false,
  })

  const emit = defineEmits<Emits>()
  const { t } = useI18n()
  const gaService = useGoogleAnalytics()
  const discountScheme = computed(() => ({
    discounts: props.plan.discounts,
    price: props.plan.price,
  }))
  const { discount } = useDiscount(discountScheme)
  const userStore = useUserStore()

  const travelersCount = computed(() => userStore.adultsCount + userStore.childrenCount)

  const currency = computed(() =>
    props.typeOfTravel === TravelDirection.OUTBOUND ? t('usd') : t('qar')
  )

  const handleToggle = (isOpen: boolean, rowId: string) => {
    const row = rows.value.find((r) => r.id === rowId)
    if (row) {
      emit(TOGGLE_EVENT_NAME, isOpen, row.id, row.title)
    }
  }

  const priceFromId = (id: string) => props.plan.ids.find((value) => value.id === id)?.value!

  const rows = computed(() => {
    const includedCovers = props.plan.included.map((cover) => ({
      title: cover.name,
      id: cover.id,
      content: cover.description,
      isAdditional: false,
      benefits: cover.benefits.map((benefit) => {
        const price = benefit.price.replace(/,/, '') // Price is: 1,000,000, that is string type. To check it for NaN we need to remove commas.
        return {
          title: benefit.name,
          description: benefit.description,
          price: isNaN(Number(price)) ? undefined : benefit.price,
          currency: currency.value,
        }
      }),
      subtitles: cover.subtitles,
    }))

    const additionalCovers = props.withAddons
      ? props.plan.ids.map((cover) => ({
          title: cover.id === CYBER_ADDON_ID ? t('outbound.cyber_security_addon.name') : cover.name,
          id: cover.id,
          content: cover.description,
          isAdditional: true,
        }))
      : []

    return [...includedCovers, ...additionalCovers]
  })

  const title = computed(() => props.title ?? props.plan.name)

  const sendCyberTooltipOpen = () => {
    gaService.sendCyberTooltip(
      TravelDirection.OUTBOUND,
      'select_plan',
      t('addon.cyber.excess.info')
    )
  }
</script>

<template>
  <!-- vue-ingore -->
  <div class="flow-accordion">
    <div v-if="discount.percentage || $slots.chips" class="flow-accordion__chips">
      <BaseChip v-if="discount.percentage" variant="green">
        {{ discount.name }} -<span class="ltr">{{ discount.percentage }}%</span>
      </BaseChip>
      <slot name="chips" />
    </div>

    <BaseAccordion
      class="flow-accordion__accordion"
      :title="title"
      :rows="rows"
      @toggle="handleToggle"
    >
      <template #before-title>
        <slot name="before-title" />
      </template>

      <template v-if="props.withPrice" #after-title>
        <slot name="after-title">
          <div class="flow-accordion__price">
            <div v-if="discount.amount > 0" class="flow-accordion__discount ltr">
              {{ $t('qar') }}
              {{ toFixedNumber(props.plan.price + discount.amount) }}
            </div>
            <div class="ltr">
              {{ $t('qar') }}
              {{ toFixedNumber(props.plan.price) }}
            </div>
          </div>
        </slot>
      </template>

      <template #content="row">
        <slot name="content" v-bind="row">
          <template v-if="row.id === CYBER_ADDON_ID">
            {{ $t('outbound.cyber_security_addon.desc_1') }}
            <strong>{{ $t('outbound.cyber_security_addon.desc_2') }}</strong>
            {{ $t('outbound.cyber_security_addon.desc_3') }}
            <CyberSecurityButton />
          </template>

          <template v-else>
            {{ row.content }}
          </template>
        </slot>
        <div
          v-if="
            //@ts-ignore
            row.benefits?.length > 0
          "
          class="flow-accordion__benefits"
        >
          <FlowAccordionBenefit
            v-for="//@ts-ignore
                        benefit in row.benefits"
            :key="benefit.title"
            :benefit="benefit"
          />
        </div>

        <AddonText
          v-if="row.isAdditional"
          :id="row.id"
          :price="priceFromId(row.id)"
          :travelers-count="travelersCount"
          @open-cyber-tooltip="sendCyberTooltipOpen"
        />
      </template>

      <template #after-content="row">
        <div v-if="row.subtitles && row.subtitles.length" class="flow-accordion__subtitles">
          <div>{{ row.subtitles[0] }}</div>
          <div v-if="row.subtitles[1]" class="flow-accordion__subtitles-1">
            {{ row.subtitles[1] }}
          </div>
        </div>

        <BaseChip v-if="row.isAdditional" variant="purple" class="addon-chips">
          {{ $t('outbound.plans.plan_information.addon_badge') }}
        </BaseChip>
      </template>

      <slot name="footer"></slot>
    </BaseAccordion>
  </div>
</template>

<style lang="scss" scoped>
  @import './FlowAccordion';
</style>
